import { useNavigate } from 'react-router';
import { Checkbox, Stack } from '@chakra-ui/react';
import type { GroupInfo, GroupMember } from '@halo/chat-sdk-react';
import { useAppStore } from '@halo/chat-sdk-react';
import {
  ButtonRed,
  ConfirmDialog,
  DisbandIcon,
  LogoutIcon,
  tools,
  AssignTransferIcon,
  TransferGroupOwner,
} from '@halo/ui-chat-react';
import Paths from '@/configs/router/paths';
import { usePortalContainer } from '@/scripts/hooks';

export interface FooterButtonsProps {
  setOpenInfoDrawer: (open: boolean) => void;
  groupInfo?: GroupInfo | null;
  isGroupOwner?: boolean;
  isGroupManager?: boolean;
  members?: GroupMember[];
  onTransferGroupOwnerClick: (member: GroupMember) => Promise<any>;
}

const FooterButtons = ({
  members,
  groupInfo,
  isGroupOwner,
  setOpenInfoDrawer,
  onTransferGroupOwnerClick,
}: FooterButtonsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [leaveLoading, setLeaveLoading] = useState(false);
  const [disbandLoading, setDisbandLoading] = useState(false);
  const [openDisband, setOpenDisband] = useState(false);
  // const [deleteChatHistory, setDeleteChatHistory] = useState(true);
  const [clearChatHistory, setClearChatHistory] = useState(true);
  const [openLeave, setOpenLeave] = useState(false);

  const { client, hideSession } = useAppStore();

  const { portalContainerRef } = usePortalContainer();

  const onLeaveGroup = useCallback(() => {
    if (leaveLoading) return;
    setLeaveLoading(true);
    client?.Api.session
      .quitGroup(groupInfo?.gid ?? '')
      .then(async ({ quit_fail_list }) => {
        setOpenInfoDrawer(false);
        if (quit_fail_list.length > 0) {
          return tools.toaster.error({
            title: t('chatSettings.failedToLeaveGroup'),
          });
        }
        if (clearChatHistory) {
          await hideSession(groupInfo?.gid ?? '', clearChatHistory);
          navigate(Paths.MESSAGES_CHATS);
        }
      })
      .finally(() => {
        setLeaveLoading(false);
      });
  }, [
    leaveLoading,
    client?.Api.session,
    groupInfo?.gid,
    setOpenInfoDrawer,
    clearChatHistory,
    hideSession,
    navigate,
    t,
  ]);

  const onDisbandGroup = useCallback(async () => {
    if (disbandLoading || !groupInfo) return;
    setDisbandLoading(true);
    client?.Api.session
      .disbandGroup(groupInfo?.gid ?? '')
      .then(async () => {
        setOpenInfoDrawer(false);
        tools.toaster.success({
          title: t('chatSettings.disbandGroupSuccessfully'),
        });
        // if (deleteChatHistory) {
        //   await hideSession(groupInfo.gid, deleteChatHistory);
        //   navigate(Paths.MESSAGES_CHATS);
        // }
      })
      .catch((err) => {
        tools.toaster.error({
          title: `${err.code}: ${err.message}`,
        });
      })
      .finally(() => {
        setDisbandLoading(false);
      });
  }, [disbandLoading, groupInfo, client?.Api.session, setOpenInfoDrawer, t]);

  return (
    <Stack gap="2" p="4" pt="5" fontSize="sm">
      {isGroupOwner ? (
        <ButtonRed
          variant="outline"
          borderRadius="xl"
          gap="1"
          disabled={disbandLoading}
          onClick={() => {
            setOpenDisband(true);
          }}
        >
          <DisbandIcon />
          {t('chatSettings.disbandGroup')}
        </ButtonRed>
      ) : (
        <ButtonRed
          variant="outline"
          borderRadius="xl"
          gap="1"
          disabled={leaveLoading}
          onClick={() => {
            setOpenLeave(true);
          }}
        >
          <LogoutIcon />
          {t('chatSettings.leaveGroup')}
        </ButtonRed>
      )}
      {isGroupOwner && (
        <TransferGroupOwner
          trigger={
            <ButtonRed variant="outline" borderRadius="xl" gap="1" onClick={() => {}}>
              <AssignTransferIcon />
              {t('chatSettings.assignAsGroupOwner')}
            </ButtonRed>
          }
          members={members ?? []}
          onTransferClick={onTransferGroupOwnerClick}
        />
      )}

      <ConfirmDialog
        disabledPortal={false}
        portalContainerRef={portalContainerRef}
        open={openDisband}
        onClose={() => setOpenDisband(false)}
        title={t('chatSettings.disbandGroup')}
        subTitle={t('chatSettings.disbandGroupSubTitle')}
        onOk={() => onDisbandGroup()}
        okText={t('buttons.confirm')}
      >
        {/* <Checkbox.Root
          size="sm"
          defaultChecked={deleteChatHistory}
          onCheckedChange={(e) => setDeleteChatHistory(!!e.checked)}
          cursor="pointer"
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control cursor="pointer" borderRadius="sm" />
          <Checkbox.Label>Clear chat history for everyone</Checkbox.Label>
        </Checkbox.Root> */}
      </ConfirmDialog>
      <ConfirmDialog
        disabledPortal={false}
        portalContainerRef={portalContainerRef}
        open={openLeave}
        onClose={() => setOpenLeave(false)}
        title={t('chatSettings.leavingGroup', { name: groupInfo?.group_name })}
        onOk={() => onLeaveGroup()}
        okText={t('buttons.leave')}
      >
        <Checkbox.Root
          size="sm"
          defaultChecked={clearChatHistory}
          onCheckedChange={(e) => setClearChatHistory(!!e.checked)}
          cursor="pointer"
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control cursor="pointer" borderRadius="sm" />
          <Checkbox.Label>{t('chatSettings.clearChatHistory')}</Checkbox.Label>
        </Checkbox.Root>
      </ConfirmDialog>
    </Stack>
  );
};

const MemoizedFooterButtons = memo(FooterButtons);
export { MemoizedFooterButtons as FooterButtons };
